<template>
  <div v-if="(permittedSubscriptions.length && availableSubscriptionTools.includes('tracker') && hasTrackerAccess) || getRight('superadmin')" class="card">
    <div class="card-header tracker_header">
      <div class="logo">
        <img src="@/assets/images/mood_tracker/tracker_logo.svg" height="50px">
      </div>
      <div class="special_header">
        &ndash; ваш персональный помощник в принятии решений на основе
        эмоционального интеллекта (ЭИ)
      </div>
    </div>

    <div class="card-body">
      <div class="description">
        <h4 class="title">Поддержка принятия решений с опорой на ресурс эмоций</h4>

        <ul class="list">
          <li>&bull; Помогает экономить время и меньше уставать,</li>
          <li>&bull; Учит грамотно использовать эмоции (и не только свои) чтобы выполнять работу быстрее и качественнее,</li>
          <li>&bull; Подсказывает как действовать в различных ситуациях, гибко применять ресурс эмоций чтобы достигать целей,</li>
          <li>&bull; Предотвращает эмоциональное выгорание</li>
        </ul>

        <div class="block-green">
          <p>
            Эмоции — это не просто данные. Они влияют на то, как вы думаете, принимаете решения, действуете и взаимодействуете с другими.
            Какие–то эмоции чувствовать приятно. Например, радость. А есть такие, которые не так приятны. Например, гнев.
            Но даже у самой неприятной эмоции есть своя роль, функция. Так было заложено эволюционно. Эмоции помогают отстаивать границы,
            налаживать связи, избегать опасности и многое другое.
          </p>
        </div>

      </div>

      <div class="instruction_wrapper">
        <h4>Эмоции могут стать вашим ресурсом и помогать в определенной деятельности</h4>
        <button v-if="!showInstruction"
                @click="showInstruction = !showInstruction" class="btn settings_btn">
          Инструкция
        </button>
      </div>

      <div class="main_instruction_wrapper" v-if="showInstruction">
        <MoodTrackerInstruction />
        <button v-if="showInstruction"
                @click="showInstruction = !showInstruction" class="btn hide_button">
          Скрыть
        </button>
      </div>

      <mood-tracker-form/>
    </div>
  </div>
  <div v-else class="subscription-required">
    Чтобы использовать этот инструмент, необходимо оформить подписку. Пожалуйста, сделайте это для получения доступа.
  </div>

  <pop-up-tracker-feedback v-if="feedbackRequired && feedbackRecord" @close="resetFeedbackRecord" :recordId="feedbackRecord?.id" />
</template>

<script>
import MoodTrackerForm from "@/views/MoodTracker/MoodTrackerForm.vue";
import useCheckRights from "@/mixins/useCheckRights";
import {computed, ref} from "@vue/reactivity";
import {onBeforeMount, watch} from "@vue/runtime-core";
import {useStore} from "vuex";
import MoodTrackerInstruction from "@/views/MoodTracker/MoodTrackerInstruction.vue";
import PopUpTrackerFeedback from "@/views/MoodTracker/MoodTrackerFeedbackPopup.vue";

export default {
  name: "MoodTrackerPage",
  components: {PopUpTrackerFeedback, MoodTrackerInstruction, MoodTrackerForm},
  setup() {
    const store = useStore();
    const {getRight} = useCheckRights();
    const user = computed(() => store.state.profile.profile);

    const permittedSubscriptions = computed(() => store.state.subscription.permittedSubscriptions);
    const records = computed(() => store.state.tracker.recordList);
    const availableSubscriptionTools = ref([]);
    const hasTrackerAccess = ref(false);
    const showInstruction = ref(false);
    const feedbackRequired = ref(false);
    const feedbackRecord = ref();

    const checkInstrumentAccess = () => {
      permittedSubscriptions.value.map((subscription) => {
        if (subscription.instruments.length) {
          subscription.instruments.forEach((instrument) => {
            if (!availableSubscriptionTools.value.includes(instrument)) {
              availableSubscriptionTools.value.push(instrument);
            }
          });
        }
        if (subscription.staff_instruments_access && !hasTrackerAccess.value) {
          hasTrackerAccess.value = subscription.staff_instruments_access.tracker.find((staffId) => staffId === user.value.id)
        }
      })

      if (getRight('private_person') && !hasTrackerAccess.value) {
        hasTrackerAccess.value = availableSubscriptionTools.value.includes('tracker');
      }
    }

    const checkTimeForFeedback = () => {
      if (!feedbackRecord.value?.feedback_interval) {
        return;
      }

      const now = new Date();
      let periods = feedbackRecord.value.feedback_interval.split('-');

      let startPeriod = new Date();
      let startPeriodHours = periods[0].split(':')[0];
      let startPeriodMinutes = periods[0].split(':')[1];
      startPeriod.setHours(startPeriodHours, startPeriodMinutes, 0);

      let endPeriod = new Date();
      let endPeriodHours = periods[1].split(':')[0];
      let endPeriodMinutes = periods[1].split(':')[1];
      endPeriod.setHours(endPeriodHours, endPeriodMinutes, 59);

      if (now >= startPeriod && now <= endPeriod && !feedbackRequired.value) {
        feedbackRequired.value = true;
      }
    }

    onBeforeMount(() => {
      if (!permittedSubscriptions.value) {
        if (getRight('subscription') && user.value?.id) {
          store.dispatch('subscription/getPermittedSubscriptions', user.value?.company?.id ?? null);
        }
      } else {
        checkInstrumentAccess();
      }

      store.dispatch('tracker/getRecords');
    });

    const checkRecordsFeedback = () => {
      records.value?.pending_feedback.map((record) => {
        if (record.feedback_interval) {
          feedbackRecord.value = record;
          checkTimeForFeedback();

          let interval;
          if (!feedbackRequired.value) {
            interval = setInterval(() => {
              if (feedbackRecord.value) {
                checkTimeForFeedback();
              } else {
                clearInterval(interval);
              }
            }, 30000);
          }
        } else {
          feedbackRecord.value = record;
          feedbackRequired.value = true;
        }
      })
    }

    const resetFeedbackRecord = () => {
      feedbackRecord.value = null;
      feedbackRequired.value = false;
    }

    watch(() => permittedSubscriptions.value, () => {
      checkInstrumentAccess();
    });

    watch(() => records.value, () => {
      checkRecordsFeedback();
    })

    return {
      getRight,
      permittedSubscriptions,
      availableSubscriptionTools,
      hasTrackerAccess,
      showInstruction,
      feedbackRequired,
      feedbackRecord,
      resetFeedbackRecord,
    }
  }
}
</script>

<style scoped lang="scss">

.title {
  text-transform: uppercase;
  font-size: 20px;
  font-weight: bold;
  color: #51bb50;
}

.header_green {
  font-weight: bold;
  color: #51bb50;
}

.list {
  margin: 25px 0 25px 0;
}

.block-green {
  background-color: #51bb50;
  padding: 20px;
  color: #fff;
  text-align: justify;
}

.instruction_wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 20px;
  margin: 25px 0;
  min-height: 40px;
}

.instruction_wrapper h4 {
  font-weight: bold;
}

.instruction_wrapper button {
  border: 1px solid #000;
  padding: 10px;
  text-transform: uppercase;
}

.settings_btn {
  cursor: pointer;
  border: 1px solid #000;
}

.settings_btn {
  text-transform: uppercase;
}

.settings_btn:hover {
  background-color: #cccccc;
  text-transform: uppercase;
}

.main_instruction_wrapper {
  margin: 20px 0;
  position: relative;
  padding: 30px;
  border: 1px solid #000000;
}

.hide_button {
  position: absolute;
  top: 15px;
  right: 30px;
  background-color: #edf2f8;
  cursor: pointer;
}

.hide_button:hover {
  opacity: 0.8;
}

.info_img_wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.info_img {
  width: 100%;
  height: auto;
  max-width: 100%;
}

.mobile {
  display: none;
}

@media (max-width: 1100px) {
  .desktop {
    display: none;
  }
  .mobile {
    display: flex;
  }
}

.subscription-required {
  padding-bottom: 30px;
}

.analytics_button_wrapper {
  padding: 30px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 20px;
}

.tracker_header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 20px;
}

.special_header {
  align-self: flex-end;
  font-size: 18px;
  font-weight: bold;
}

.blue {
  background-color: #183695;
}

.red {
  background-color: #fd0a07;
}

.green {
  background-color: #6dba33;
}

.yellow {
  background-color: #fde80c;
}
</style>